import React, { useContext, useEffect, useState } from "react";
import ErrorBoundary from "./ErrorBoundary";
import { IAuditExtended, IOperationRequestBase, ISyncTask, SyncType, TaskStatus, SubscriptionEventType, syncPermission } from "@symity-hub/types";
import { Divider, Link, Menu, MenuButtonProps, MenuItem, MenuList, MenuPopover, MenuTrigger, Spinner, SplitButton, Text, makeStyles, shorthands, tokens } from "@fluentui/react-components";
import { ArrowSync24Regular, ArrowSyncDismiss24Regular } from "@fluentui/react-icons";
import { RbacWrapper } from "./RbacWrapper";
import { useMsal } from "@azure/msal-react";
import { fetcher } from "../modules/api";
import { AppContext } from "../contexts/AppContext";
import { DateTime } from "luxon";
import { sharedHorizontalExtraSmallGapFlexStyles } from "../styles/styles";
import { StatusIcon } from "./StatusIcon";
import { AuditPanel } from "./AuditPanel";
import { v4 as uuidv4 } from "uuid";

const syncStyles = makeStyles({
    root: {
        ...shorthands.padding(tokens.spacingHorizontalS, tokens.spacingVerticalS)
    },
    syncButton: {
        marginLeft: "auto"
    }
});

const appAlertName = "syncOutOfDate";

export const Sync: React.FunctionComponent = () => {

    const msalContext = useMsal();
    const appContext = useContext(AppContext);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [syncInProgress, setSyncInProgress] = useState<boolean>(true);
    const [syncOutOfDate, setSyncOutOfDate] = useState<boolean>(false);
    const [data, setData] = useState<IAuditExtended>();
    const [panelOpen, setPanelOpen] = useState<boolean>(false);

    const callWebSocket = async () => {
        try {
            if (!appContext.sendMessage) {
                console.log("sendMessage not available in AppContext");
                return;
            }

            setIsLoading(true);
            await appContext.sendMessage({
                id: uuidv4(),
                type: SubscriptionEventType.SyncStatus,
                updateFrequency: 10000
            }, (response) => {
                if (response && response.data && response.data.resources.length > 0) {
                    const latest = response.data.resources[0] as IAuditExtended;
                    // Check if the latest sync is out of date (older than 24 hours)
                    if (DateTime.fromISO(latest.operation?.lastUpdatedTime as unknown as string).diffNow("hours").hours < -24) {
                        setSyncOutOfDate(true);
                        if (!appContext.alerts.some(alert => alert.id === appAlertName)) {
                            appContext.setAlerts([...appContext.alerts, {
                                id: appAlertName,
                                text: "Sync is out of date. Please initiate a new sync to ensure information is up-to-date.",
                                type: TaskStatus.Warning
                            }]);
                        }
                    } else {
                        appContext.setAlerts(appContext.alerts.filter((alert) => alert.id !== appAlertName));
                        setSyncOutOfDate(false);
                    }
                    // Check if sync is in progress
                    if (latest.operation?.runtimeStatus === "Running" || latest.operation?.runtimeStatus === "Pending") {
                        setSyncInProgress(true);
                    } else {
                        setSyncInProgress(false);
                    }
                    // Set the latest sync data
                    setData(latest);
                } else {
                    setSyncInProgress(false);
                }
                setIsLoading(false);
            });
        } catch (error) {
            console.error('WebSocket Error:', error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        callWebSocket();
    }, [appContext.sendMessage]);

    const onSyncClick = async (syncType: SyncType) => {
        if (syncInProgress && data && data.operation) {
            await fetcher([`/api/operations/${data.operation.instanceId}/terminate`, msalContext, "POST"])
        } else {
            const tasks: ISyncTask[] = [
                {
                    name: "syncGraphUsers",
                    syncType
                },
                {
                    name: "syncTeamsPhoneNumberAssignments",
                    syncType: SyncType.Full
                }
            ];
            const operationRequest: IOperationRequestBase = {
                tasks
            };
            await fetcher(["/api/operations/sync", msalContext, "POST", undefined, operationRequest])
                .then(() => {
                    appContext.notify("Sync started successfully.", "success");
                })
                .catch((error) => {
                    console.error(error);
                    appContext.notify("Error starting sync, please try again.", "error");
                });
        }
    }

    const onDismiss = () => {
        setPanelOpen(false);
    }

    const primaryActionButtonProps = {
        onClick: () => onSyncClick(SyncType.Delta)
    };

    return (
        <ErrorBoundary>
            <Divider />
            <div
                className={syncStyles().root}
            >
                {isLoading
                    ? <Spinner
                        size="extra-small"
                        label="Getting sync status..."
                    />
                    : (
                        <div>
                            <div
                                className={sharedHorizontalExtraSmallGapFlexStyles().root}
                            >
                                <StatusIcon status={
                                    syncOutOfDate
                                        ? TaskStatus.Warning
                                        : data?.operation?.runtimeStatus as string
                                }
                                />
                                <Link
                                    onClick={() => setPanelOpen(true)}
                                >
                                    <Text size={400}>{
                                        syncInProgress
                                            ? "Syncing"
                                            : "Last sync"
                                    }
                                    </Text>
                                </Link>
                                <div
                                    className={syncStyles().syncButton}
                                >
                                    <RbacWrapper allowedPermissions={[syncPermission]}>
                                        <Menu positioning="below-end">
                                            <MenuTrigger disableButtonEnhancement>
                                                {(triggerProps: MenuButtonProps) => (
                                                    <SplitButton
                                                        menuButton={triggerProps}
                                                        primaryActionButton={primaryActionButtonProps}
                                                        size="small"
                                                        icon={syncInProgress ? <ArrowSyncDismiss24Regular /> : <ArrowSync24Regular />}
                                                    >
                                                        {syncInProgress
                                                            ? "Stop"
                                                            : "Sync"
                                                        }
                                                    </SplitButton>
                                                )}
                                            </MenuTrigger>
                                            <MenuPopover>
                                                <MenuList>
                                                    <MenuItem
                                                        onClick={() => onSyncClick(SyncType.Full)}
                                                    >Full sync</MenuItem>
                                                </MenuList>
                                            </MenuPopover>
                                        </Menu>
                                    </RbacWrapper>
                                </div>
                            </div>
                            <Text>
                                {data && data.operation
                                    ? syncInProgress
                                        ? `${data.operation.runtimeStatus as string}, Started: ${DateTime.fromISO(data.operation.createdTime as unknown as string).toRelative()}`
                                        : `${data.operation.runtimeStatus}, ${DateTime.fromISO(data.operation.lastUpdatedTime as unknown as string).toRelative()}`
                                    : "No sync information available"
                                }
                            </Text>
                        </div>
                    )}
            </div>
            {panelOpen && data && (
                <AuditPanel
                    open={panelOpen}
                    setOpen={setPanelOpen}
                    data={data}
                    onDismiss={onDismiss}
                />
            )}
        </ErrorBoundary>
    );
}
